import { AppUser } from 'models/AppUser';
import { AppUserFilter } from 'models/AppUserFilter';
import { LdapUser } from 'models/LdapUser';
import { LdapUserFilter } from 'models/LdapUserFilter';
import { Role } from 'models/Role';
import { RoleFilter } from 'models/RoleFilter';
import React, { ChangeEvent, Dispatch, SetStateAction } from 'react';
import { v4 as uuidv4 } from 'uuid';

export class AppUserService {
  public useAppUserModal(
    getList: (filter: AppUserFilter) => Promise<AppUser[]>,
    count: (filter: AppUserFilter) => Promise<number>,
    model: AppUser,
  ): [
    boolean,
    Dispatch<SetStateAction<boolean>>,
    boolean,
    Dispatch<SetStateAction<boolean>>,
    AppUser[],
    number,
  ] {
    const [visible, setVisible] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [list, setList] = React.useState<AppUser[]>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [filter] = React.useState<AppUserFilter>(new AppUserFilter());

    React.useEffect(() => {
      if (visible) {
        setLoading(true);
        Promise.all([getList(filter), count(filter)])
          .then(([list, total]) => {
            setList(list);
            setTotal(total);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }, [count, filter, getList, model, visible]);

    return [loading, setLoading, visible, setVisible, list, total];
  }

  public useRoleContentMaster(
    getList: (filter: RoleFilter) => Promise<Role[]>,
    count: (filter: RoleFilter) => Promise<number>,
  ): [
    RoleFilter,
    Dispatch<SetStateAction<RoleFilter>>,
    Role[],
    Dispatch<SetStateAction<Role[]>>,
    boolean,
    Dispatch<SetStateAction<boolean>>,
    () => void,
    number,
  ] {
    const [filter, setFilter] = React.useState<RoleFilter>(new RoleFilter());
    const [loading, setLoading] = React.useState<boolean>(true);
    const [loadList, setLoadList] = React.useState<boolean>(true);
    const [list, setList] = React.useState<Role[]>([]);
    const [total, setTotal] = React.useState<number>(0);

    React.useEffect(() => {
      if (loadList) {
        setLoading(true);
        Promise.all([getList(filter), count(filter)])
          .then(([list, total]) => {
            setList(list);
            setTotal(total);
          })
          .finally(() => {
            setLoadList(false);
            setLoading(false);
          });
      }
    }, [count, filter, getList, loadList]);

    const handleSearch = React.useCallback(() => {
      setLoadList(true);
    }, [setLoadList]);
    return [
      filter,
      setFilter,
      list,
      setList,
      loading,
      setLoading,
      handleSearch,
      total,
    ];
  }

  public useAppUserLapModal(
    model?: AppUser,
    setModel?: Dispatch<SetStateAction<AppUser>>,
    getList?: (filter: LdapUserFilter) => Promise<LdapUser[]>,
  ) {
    const [ldapUserFilter, setLdapUserFilter] = React.useState<LdapUserFilter>(
      new LdapUserFilter(),
    );
    const [listLdapUser, setListLdapUser] = React.useState<LdapUser[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [isOpenLdap, setOpenLdap] = React.useState<boolean>(false);
    const [currentLdapUser, setCurrentLdapUser] = React.useState<LdapUser[]>(
      [],
    );
    const handleGetData = React.useCallback(() => {
      setLoading(true);
      Promise.all([getList(ldapUserFilter)])
        .then(([list]) => {
          const arr = list.map(user => {
            user.key = uuidv4();
            return user;
          });
          setListLdapUser([...arr]);
          if (model?.id) {
            arr.forEach(user => {
              if (user.ldapUsername === model.username) {
                setCurrentLdapUser([user]);
              }
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }, [ldapUserFilter, getList, model]);
    const handleOpenLdapModal = React.useCallback(() => {
      setOpenLdap(true);
      handleGetData();
    }, [handleGetData]);
    const handleCloseLdapModal = React.useCallback(() => {
      setOpenLdap(false);
    }, []);

    const handleFilter = React.useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        ldapUserFilter.ldapUsername = event.target.value;
        setLdapUserFilter({ ...ldapUserFilter });
        handleGetData();
      },
      [ldapUserFilter, handleGetData],
    );

    const handleSaveModal = React.useCallback(
      event => {
        setOpenLdap(false);
        const user = { ...model };
        user.phone = event[0]?.phone;
        user.email = event[0]?.email;
        user.displayName = event[0]?.displayName;
        user.username = event[0]?.ldapUsername;
        setModel(user);
        setCurrentLdapUser([event[0]]);
      },
      [setModel, model],
    );

    return {
      loading,
      listLdapUser,
      handleOpenLdapModal,
      handleCloseLdapModal,
      handleFilter,
      handleSaveModal,
      ldapUserFilter,
      setLdapUserFilter,
      isOpenLdap,
      currentLdapUser,
      setCurrentLdapUser,
    };
  } // for modal component
}
export const appUserService: AppUserService = new AppUserService();
